import type { Database } from '@planmax/shared-types'
import type { RecordStatusFilter } from '@/types/record-status'

// Database row type
export type AircraftRow = Database['public']['Tables']['aircraft']['Row']
export type AircraftInsert = Database['public']['Tables']['aircraft']['Insert']
export type AircraftUpdate = Database['public']['Tables']['aircraft']['Update']

// Status enum
export type AircraftStatus = Database['public']['Enums']['aircraft_status']

export const AIRCRAFT_STATUSES = [
  'active',
  'maintenance',
  'storage',
  'departed',
  'inactive',
] as const

export const AIRCRAFT_STATUS_LABELS: Record<AircraftStatus, string> = {
  active: 'Active',
  maintenance: 'Maintenance',
  storage: 'Storage',
  departed: 'Departed',
  inactive: 'Inactive',
}

// Badge variants for status display
export const AIRCRAFT_STATUS_VARIANTS: Record<AircraftStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  active: 'default',
  maintenance: 'secondary',
  storage: 'outline',
  departed: 'outline',
  inactive: 'destructive',
}

/**
 * Aircraft table row with joined data for display
 */
export interface AircraftTableRow {
  id: string
  organization_id: string
  tail_number: string
  // Aircraft type info (joined)
  aircraft_type_id: string | null
  aircraft_type_name: string | null
  aircraft_type_manufacturer: string | null
  aircraft_type_model: string | null
  aircraft_type_category: string | null
  aircraft_type_default_fuel_type: string | null
  // Manual override fields (if no type selected)
  manufacturer: string | null
  model: string | null
  // Ownership
  owner_client_id: string | null
  owner_client_name: string | null
  operator_client_id: string | null
  operator_client_name: string | null
  owner_name: string | null // Free text owner name
  // Dimensions (can be overridden from type)
  length: number | null
  wingspan: number | null
  height: number | null
  tail_height: number | null
  empty_weight: number | null
  max_takeoff_weight: number | null
  // Registration
  registration_country: string | null
  serial_number: string | null
  year_manufactured: number | null
  // Compliance
  annual_inspection_due: string | null
  insurance_expiry: string | null
  // Location
  home_location_id: string | null
  home_location_name: string | null
  current_location_id: string | null
  current_location_name: string | null
  current_area_id: string | null
  current_area_name: string | null
  // Custom 3D model
  custom_model_file_path: string | null
  custom_model_scale: number | null
  // Status & notes
  status: AircraftStatus
  notes: string | null
  settings: Record<string, unknown> | null
  // Audit
  created_at: string
  updated_at: string | null
  deleted_at: string | null
}

/**
 * Form data for aircraft create/edit
 */
export interface AircraftFormData {
  tail_number: string
  aircraft_type_id: string | null
  // Manual fields (if no type)
  manufacturer: string | null
  model: string | null
  // Ownership
  owner_client_id: string | null
  operator_client_id: string | null
  owner_name: string | null
  // Dimensions
  length: number | null
  wingspan: number | null
  height: number | null
  tail_height: number | null
  empty_weight: number | null
  max_takeoff_weight: number | null
  // Registration
  registration_country: string | null
  serial_number: string | null
  year_manufactured: number | null
  // Compliance
  annual_inspection_due: string | null
  insurance_expiry: string | null
  // Location
  home_location_id: string | null
  current_location_id: string | null
  current_area_id: string | null
  // Custom 3D model
  custom_model_file_path: string | null
  custom_model_scale: number | null
  // Default contact — not collected by AircraftForm and not persisted by
  // createAircraft/updateAircraft; optional so callers need not supply it.
  default_contact_id?: string | null
  // Status & notes
  status: AircraftStatus
  notes: string | null
}

/**
 * Filters for aircraft list queries
 */
export interface AircraftFilters {
  status?: AircraftStatus[]
  locationId?: string
  search?: string
  hasOwner?: boolean
  ownerClientId?: string
  recordStatus?: RecordStatusFilter
}

/**
 * Get display name for an aircraft (tail number + type)
 */
export function getAircraftDisplayName(aircraft: AircraftTableRow): string {
  const type = aircraft.aircraft_type_name ||
    (aircraft.manufacturer && aircraft.model
      ? `${aircraft.manufacturer} ${aircraft.model}`
      : null)

  return type
    ? `${aircraft.tail_number} (${type})`
    : aircraft.tail_number
}

// ============================================
// AIRCRAFT REGISTRY TYPES
// ============================================

/**
 * Aircraft registry tab identifiers (legacy — kept for migration)
 */
export type AircraftRegistryTab = 'on_field' | 'based' | 'all_known' | 'network'

// ============================================
// SAVED VIEW TYPES (v2.1)
// ============================================

/**
 * Saved view identifiers — pre-built workflow presets
 */
export type SavedViewId = 'ramp' | 'based_fleet' | 'transients' | 'all'

/**
 * Presence filter — where is the aircraft physically?
 */
export type PresenceFilter = 'on_field' | 'arriving' | 'departing' | 'away'

/**
 * Relationship filter — what's the business arrangement?
 */
export type RelationshipFilter = 'based' | 'transient' | 'no_contract'

/**
 * CRM stage filter — lifecycle status
 */
export type CrmStageFilter = 'active' | 'lead' | 'prospect' | 'former' | 'unknown'

/**
 * Quick filter state — independent multi-select dimensions
 */
export interface QuickFilterState {
  presence: PresenceFilter[]
  relationship: RelationshipFilter[]
  stage: CrmStageFilter[]
  /** Filter by home location (FBO base) — dynamic values from data */
  location: string[]
}

/**
 * Contextual column identifiers — auto-selected per saved view
 */
export type ContextualColumnId =
  | 'arrived_at'
  | 'etd'
  | 'days_on_field'
  | 'contract_number'
  | 'lease_expiry'
  | 'unit_assigned'
  | 'visit_count_12mo'
  | 'crm_stage'
  | 'last_visit_date'

/**
 * Saved view configuration
 */
export interface SavedViewConfig {
  id: SavedViewId
  label: string
  description: string
  defaultFilters: QuickFilterState
  contextualColumns: ContextualColumnId[]
  defaultGroupBy: GroupingOption
  /** Whether this view requires a location to be useful */
  requiresLocation: boolean
}

/**
 * Saved view definitions with default filters, columns, and grouping
 */
export const SAVED_VIEW_CONFIGS: SavedViewConfig[] = [
  {
    id: 'ramp',
    label: 'Currently On Field',
    description: 'Aircraft on the field right now — the whiteboard replacement',
    defaultFilters: {
      presence: ['on_field', 'arriving', 'departing'],
      relationship: [],
      stage: ['active'],
      location: [],
    },
    contextualColumns: ['arrived_at', 'etd', 'days_on_field'],
    defaultGroupBy: 'status',
    requiresLocation: true,
  },
  {
    id: 'based_fleet',
    label: 'Based',
    description: 'Aircraft with active leases or contracts',
    defaultFilters: {
      presence: [],
      relationship: ['based'],
      stage: ['active'],
      location: [],
    },
    contextualColumns: ['contract_number', 'lease_expiry', 'unit_assigned'],
    defaultGroupBy: 'none',
    requiresLocation: false,
  },
  {
    id: 'transients',
    label: 'Transients',
    description: 'Short-term visitors and uncontracted aircraft',
    defaultFilters: {
      presence: ['on_field', 'arriving'],
      relationship: ['transient', 'no_contract'],
      stage: ['active'],
      location: [],
    },
    contextualColumns: ['arrived_at', 'etd', 'visit_count_12mo'],
    defaultGroupBy: 'none',
    requiresLocation: true,
  },
  {
    id: 'all',
    label: 'All',
    description: 'Every aircraft in the CRM — full registry',
    defaultFilters: {
      presence: [],
      relationship: [],
      stage: [],
      location: [],
    },
    contextualColumns: ['crm_stage', 'last_visit_date'],
    defaultGroupBy: 'none',
    requiresLocation: false,
  },
]

/**
 * Filter pill option type
 */
export interface FilterPillOption<T extends string> {
  value: T
  label: string
  icon?: string
}

/**
 * Presence filter pill options
 */
export const PRESENCE_OPTIONS: FilterPillOption<PresenceFilter>[] = [
  { value: 'on_field', label: 'On Field', icon: '🟢' },
  { value: 'arriving', label: 'Arriving', icon: '✈️' },
  { value: 'departing', label: 'Departing', icon: '🛫' },
  { value: 'away', label: 'Away', icon: '⚪' },
]

/**
 * Relationship filter pill options
 */
export const RELATIONSHIP_OPTIONS: FilterPillOption<RelationshipFilter>[] = [
  { value: 'based', label: 'Based', icon: '🏠' },
  { value: 'transient', label: 'Transient', icon: '✈️' },
  { value: 'no_contract', label: 'No Contract', icon: '⚠️' },
]

/**
 * CRM stage filter pill options
 */
export const CRM_STAGE_OPTIONS: FilterPillOption<CrmStageFilter>[] = [
  { value: 'active', label: 'Active', icon: '🟢' },
  { value: 'lead', label: 'Lead', icon: '🟡' },
  { value: 'prospect', label: 'Prospect', icon: '🔵' },
  { value: 'former', label: 'Former', icon: '⚪' },
  { value: 'unknown', label: 'Unknown' },
]

/**
 * Get the default SavedViewConfig by id
 */
export function getSavedViewConfig(viewId: SavedViewId): SavedViewConfig {
  return SAVED_VIEW_CONFIGS.find((v) => v.id === viewId) ?? SAVED_VIEW_CONFIGS[3] // fallback to 'all'
}

/**
 * Tab configuration for the registry view (legacy — kept for reference)
 */
export interface AircraftRegistryTabConfig {
  value: AircraftRegistryTab
  label: string
  description?: string
  /** Whether this tab is scoped to a specific location */
  locationScoped: boolean
}

/**
 * Registry tab definitions with counts shown (legacy — kept for migration)
 */
export const AIRCRAFT_REGISTRY_TABS: AircraftRegistryTabConfig[] = [
  {
    value: 'on_field',
    label: 'On Field',
    description: 'Aircraft physically at this location now',
    locationScoped: true,
  },
  {
    value: 'based',
    label: 'Based',
    description: 'Aircraft with active leases/contracts',
    locationScoped: false,
  },
  {
    value: 'all_known',
    label: 'All Known',
    description: 'Every aircraft in the CRM',
    locationScoped: false,
  },
  {
    value: 'network',
    label: 'Network',
    description: 'Cross-location view (multi-FBO)',
    locationScoped: false,
  },
]

/**
 * Aircraft category icon groups — maps DB categories to simplified icon groups
 */
export type CategoryIconGroup = 'jet' | 'turboprop' | 'piston' | 'helo' | 'unknown'

const CATEGORY_TO_ICON_GROUP: Record<string, CategoryIconGroup> = {
  light_jet: 'jet',
  midsize_jet: 'jet',
  very_light_jet: 'jet',
  turboprop: 'turboprop',
  single_engine_piston: 'piston',
  multi_engine_piston: 'piston',
  helicopter: 'helo',
}

export function getCategoryIconGroup(category: string | null): CategoryIconGroup {
  if (!category) return 'unknown'
  return CATEGORY_TO_ICON_GROUP[category] ?? 'unknown'
}

/**
 * Grouping options for the table
 */
export type GroupingOption = 'status' | 'location' | 'relationship' | 'operator' | 'crm_stage' | 'aircraft_type' | 'none'

/**
 * Grouping option configuration
 */
export interface GroupingOptionConfig {
  value: GroupingOption
  label: string
  description: string
}

/**
 * Grouping option definitions
 */
export const GROUPING_OPTIONS: GroupingOptionConfig[] = [
  { value: 'status', label: 'By Status', description: 'Arriving / In Hangar / On Ramp / Departing' },
  { value: 'location', label: 'By Location', description: 'By building or area' },
  { value: 'relationship', label: 'By Relationship', description: 'Based / Transient / No Contract' },
  { value: 'operator', label: 'By Operator', description: 'Group by aircraft operator' },
  { value: 'crm_stage', label: 'By CRM Stage', description: 'Active / Lead / Prospect / Former' },
  { value: 'aircraft_type', label: 'By Type', description: 'Group by aircraft type' },
  { value: 'none', label: 'None', description: 'Flat list' },
]

/**
 * Anomaly flag types for inline badges
 */
export type AnomalyFlag =
  | 'no_contract'       // Yellow: transient on-field > X days without contract
  | 'insurance_expired' // Red: insurance expired
  | 'lease_expiring'    // Orange: lease expires within 30 days
  | 'no_movement'       // Gray: no movement in 90+ days (hangar queen)
  | 'repeat_visitor'    // Blue: 3+ visits, no lease (conversion opportunity)
  | 'vip_customer'      // Purple: VIP flag on customer

/**
 * Anomaly flag configuration for display
 */
export interface AnomalyFlagConfig {
  flag: AnomalyFlag
  label: string
  emoji: string
  color: 'yellow' | 'red' | 'orange' | 'gray' | 'blue' | 'purple'
  bgClass: string
  textClass: string
}

/**
 * Anomaly flag display configuration
 */
export const ANOMALY_FLAGS: AnomalyFlagConfig[] = [
  { flag: 'no_contract', label: 'No contract', emoji: '🟡', color: 'yellow', bgClass: 'bg-yellow-100 dark:bg-yellow-900/30', textClass: 'text-yellow-700 dark:text-yellow-300' },
  { flag: 'insurance_expired', label: 'Insurance expired', emoji: '🔴', color: 'red', bgClass: 'bg-red-100 dark:bg-red-900/30', textClass: 'text-red-700 dark:text-red-300' },
  { flag: 'lease_expiring', label: 'Lease expires soon', emoji: '🟠', color: 'orange', bgClass: 'bg-orange-100 dark:bg-orange-900/30', textClass: 'text-orange-700 dark:text-orange-300' },
  { flag: 'no_movement', label: 'No movement 90d', emoji: '⚪', color: 'gray', bgClass: 'bg-gray-100 dark:bg-gray-800', textClass: 'text-gray-600 dark:text-gray-400' },
  { flag: 'repeat_visitor', label: 'Repeat visitor', emoji: '🔵', color: 'blue', bgClass: 'bg-blue-100 dark:bg-blue-900/30', textClass: 'text-blue-700 dark:text-blue-300' },
  { flag: 'vip_customer', label: 'VIP', emoji: '🟣', color: 'purple', bgClass: 'bg-purple-100 dark:bg-purple-900/30', textClass: 'text-purple-700 dark:text-purple-300' },
]

/**
 * Relationship type between aircraft and FBO
 */
export type AircraftRelationship = 'based' | 'transient' | 'visitor' | 'one_time' | 'former_based' | 'unknown'

/**
 * Relationship labels for display
 */
export const RELATIONSHIP_LABELS: Record<AircraftRelationship, string> = {
  based: 'Based',
  transient: 'Transient',
  visitor: 'Visitor',
  one_time: 'One-time',
  former_based: 'Former Based',
  unknown: 'Unknown',
}

/**
 * Position status for On Field grouping
 */
export type OnFieldStatus = 'arriving' | 'in_hangar' | 'on_ramp' | 'departing'

/**
 * Position status labels
 */
export const ON_FIELD_STATUS_LABELS: Record<OnFieldStatus, string> = {
  arriving: 'Arriving Today',
  in_hangar: 'In Hangar',
  on_ramp: 'On Ramp / Tiedown',
  departing: 'Departing Today',
}

/**
 * Position status display configuration with icons
 */
export const ON_FIELD_STATUS_CONFIG: Record<OnFieldStatus, { label: string; emoji: string; order: number }> = {
  arriving: { label: 'Arriving Today', emoji: '✈️', order: 1 },
  in_hangar: { label: 'In Hangar', emoji: '🏢', order: 2 },
  on_ramp: { label: 'On Ramp / Tiedown', emoji: '🅿️', order: 3 },
  departing: { label: 'Departing Today', emoji: '🛫', order: 4 },
}

/**
 * Extended aircraft row for registry view with allocation and relationship data
 */
export interface AircraftRegistryRow extends AircraftTableRow {
  // Position data (for On Field tab)
  allocation_id: string | null
  position_type: string | null  // 'hangar' | 'ramp' | 'tiedown'
  building_name: string | null
  unit_name: string | null
  arrived_at: string | null
  scheduled_departure: string | null  // ETD

  // On-field status (computed)
  on_field_status: OnFieldStatus | null

  // Presence (computed from allocation data)
  presence: PresenceFilter

  // CRM stage (computed from relationship/activity data)
  crm_stage: CrmStageFilter

  // Relationship data
  relationship: AircraftRelationship
  lease_contract_id: string | null
  lease_contract_number: string | null
  lease_end_date: string | null
  lease_monthly_rate: number | null
  transient_stay_id: string | null
  transient_booking_number: string | null
  days_on_field: number | null

  // Business metrics (Phase 2+)
  revenue_mtd: number | null
  revenue_ytd: number | null
  ar_balance: number | null

  // Activity metrics (Phase 3)
  visits_12mo: number | null
  avg_stay_days: number | null
  last_visit_date: string | null
  fuel_purchased_ytd: number | null

  // Anomaly flags (computed client-side)
  anomalies: AnomalyFlag[]
}

/**
 * Summary data for the footer bar
 */
export interface AircraftRegistrySummary {
  totalAircraft: number
  inHangarCount: number
  onRampCount: number
  arrivingCount: number
  departingCount: number
  hangarCapacityPercent: number
  rampCapacityPercent: number
  // View-specific summary counts
  basedCount: number
  transientCount: number
  noContractCount: number
  awayCount: number
  onFieldCount: number
}

/**
 * Compute anomaly flags for an aircraft row
 */
export function computeAnomalyFlags(row: AircraftRegistryRow): AnomalyFlag[] {
  const flags: AnomalyFlag[] = []
  const now = new Date()

  // No contract: transient on-field > 5 days without lease
  if (row.relationship === 'transient' && row.days_on_field && row.days_on_field > 5 && !row.lease_contract_id) {
    flags.push('no_contract')
  }

  // Insurance expired
  if (row.insurance_expiry) {
    const expiryDate = new Date(row.insurance_expiry)
    if (expiryDate < now) {
      flags.push('insurance_expired')
    }
  }

  // Lease expiring within 30 days
  if (row.lease_end_date) {
    const leaseEnd = new Date(row.lease_end_date)
    const daysUntilExpiry = Math.ceil((leaseEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    if (daysUntilExpiry > 0 && daysUntilExpiry <= 30) {
      flags.push('lease_expiring')
    }
  }

  // Repeat visitor: 3+ visits, no lease (Phase 3)
  if (row.visits_12mo && row.visits_12mo >= 3 && !row.lease_contract_id) {
    flags.push('repeat_visitor')
  }

  return flags
}
